"use client";

import { useMemo } from "react";
import { Empty, Pagination, Spinner } from "ui-components";

import { IOrganizationsSearchParams } from "@/src/app/[locale]/(public)/organizations/page";
import {
  getOrganizationGetAllOrganizationsQueryKey,
  OrganizationGetAllOrganizationsParams,
  OrganizationGetAllOrganizationsSortOrder,
  OrganizationResponse,
  OrganizationResponseEntity,
  useOrganizationGetAllOrganizations,
} from "@/src/hooks/api/generated";
import { useNavigate } from "@/src/hooks/useNavigate";
import { usePageHref } from "@/src/hooks/usePageHref";

import { Filters } from "../enums/filters.enum";

import { OrganizationCard } from "./OrganizationCard";
import { OrganizationListingHeader } from "./OrganizationListingHeader";

const PAGE_SIZE = 9;

interface IOrganizationsListingPageProps {
  searchParams: IOrganizationsSearchParams;
  initialOrganizations?: OrganizationResponse | null;
}

export function OrganizationsListingPage({
  searchParams,
  initialOrganizations,
}: IOrganizationsListingPageProps): JSX.Element {
  const { pushQueryParam } = useNavigate();
  const page = searchParams[Filters.page] ?? "1";
  const searchTerm = searchParams[Filters.search] ?? undefined;
  const buildPageHref = usePageHref();

  const params = useMemo(() => {
    const sortBy = searchParams[Filters.sortBy];
    const raw: OrganizationGetAllOrganizationsParams = {
      page,
      pageSize: String(PAGE_SIZE),
      searchTerm,
      industryId: searchParams[Filters.industry] || undefined,
      sortOrder:
        sortBy === OrganizationGetAllOrganizationsSortOrder.ASC ||
        sortBy === OrganizationGetAllOrganizationsSortOrder.DESC
          ? (sortBy as OrganizationGetAllOrganizationsSortOrder)
          : undefined,
    };
    return Object.fromEntries(
      Object.entries(raw).filter(([, v]) => v !== undefined && v !== "")
    ) as OrganizationGetAllOrganizationsParams;
  }, [page, searchTerm, searchParams]);

  // Hydrate from the server-fetched payload via initialData (same approach as
  // the Events/Jobs listings) so the first client render matches SSR and
  // doesn't refetch. Changing filters produces a new queryKey with no
  // initialData, which triggers a normal fetch.
  const { data: organizationsData, status } =
    useOrganizationGetAllOrganizations<OrganizationResponse>(params, {
      query: {
        queryKey: getOrganizationGetAllOrganizationsQueryKey(params),
        initialData: initialOrganizations ?? undefined,
        initialDataUpdatedAt: initialOrganizations ? Date.now() : undefined,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
      },
    }) as { data: OrganizationResponse; status: string };

  return (
    <>
      <OrganizationListingHeader
        searchParams={searchParams}
        isLoading={searchTerm !== "" && status === "pending"}
      />
      <div className="my-20">
        {organizationsData?.data.length === 0 && (
          <Empty text="No organizations found." />
        )}
        {status === "pending" &&
          page === "1" &&
          !organizationsData?.data.length && (
            <div className="flex justify-center my-5">
              <Spinner size="lg" intent="brand-primary" />
            </div>
          )}
        {organizationsData?.data.length ? (
          <>
            <div className="w-full flex flex-wrap justify-center md:justify-start items-center -mt-8 mx-auto">
              {organizationsData?.data.map(
                (organization: OrganizationResponseEntity) => (
                  <OrganizationCard
                    organization={organization}
                    key={organization.id}
                  />
                )
              )}
            </div>
            {!!organizationsData?.total && organizationsData?.total > 0 && (
              <div className="mt-10">
                <Pagination
                  currentPage={page ? parseInt(page) : 1}
                  totalPages={Math.ceil(
                    (organizationsData?.total ?? 0) / PAGE_SIZE
                  )}
                  totalRecords={organizationsData?.total ?? 0}
                  getPageHref={(newPageNumber) =>
                    buildPageHref(Filters.page, newPageNumber)
                  }
                  onPageChange={(newPageNumber) => {
                    pushQueryParam({
                      key: Filters.page,
                      value: newPageNumber.toString(),
                    });
                  }}
                />
              </div>
            )}
          </>
        ) : null}
      </div>
    </>
  );
}
